import { useLayoutEffect, useRef, useState } from 'react'

import useCurrentLocationContext from '@contexts/currentLocationContext'
import useMapDataContext from '@contexts/mapDataContext'
import { useAuthContext } from '@contexts/authContext'
import styled from '@emotion/styled/macro'

import LocationPermissionDenied from '@components/layout/screen_loaders/LocationPermissionDenied'
import { MapLoader } from '@components/layout/screen_loaders/MapLoader'
import { MapContainer } from '@components/nearby/MapContainer'
import HiddenDisplay from '@components/nearby/HiddenDisplay'
import NearbyTopNav from '@components/nearby/NearbyTopNav'

import 'mapbox-gl/dist/mapbox-gl.css'

const PageContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`

const LocationDeniedContainer = styled(MapContainer)`
  background-color: black;
`

const Nearby = () => {
  const { setMapContainer, mapContainer, setTrueMapContainerVisible, map, mapDataIsInitialized, profilesInitialized, userOnMapScreen, isWebsocketConnected } = useMapDataContext()
  const [isMapLoadedInContainer, setIsMapLoadedInContainer] = useState(false)
  const [isNewLocalContainerSet, setIsNewLocalContainerSet] = useState(false)
  const { isLocationPermissionDenied } = useCurrentLocationContext()
  const { showHiddenDisplay } = useAuthContext()

  // Set map every time user enters the map screen, and redundancy to make sure the map is visible
  // NOTE: Sometimes the map still isn't visible despite the above code. the profilesInitialized
  //       useEffect doesn't re-run when the user enters the map screen, but this will always fire, making it a good fallback
  useLayoutEffect(() => {
    // Short circuit if global data isn't set
    if (!map || !mapDataIsInitialized || !mapContainer) return;
    // short circuit if the new container isn't set yet, or if the map is already loaded into the new container
    if (!isNewLocalContainerSet || isMapLoadedInContainer) return;
    // Short circuit if the user is hidden
    if (showHiddenDisplay) return;
    
    // Replace the local map with the stored map upon initialization
    mapContainer?.replaceWith?.(map?.getContainer?.())
    
    // redundancy to make sure the map is always visible
    setTrueMapContainerVisible()
    
    // NOTE: The map can't detect the size of the parent offscreen, and the canvas gets set to default 300pxx400px value
    //       This causes the map to resize when the screen loads so it can properly size to the parent element.
    map.resize()
    
    // Internal state to make sure we don't run code more than once per map screen load
    setIsMapLoadedInContainer(true)
  }, [userOnMapScreen, map, showHiddenDisplay, mapDataIsInitialized, mapContainer, isMapLoadedInContainer, isNewLocalContainerSet])

  // Map related cleanup for when we enter and exit hidden mode
  useLayoutEffect(() => {
    if (showHiddenDisplay) {
      setIsMapLoadedInContainer(false)
      setIsNewLocalContainerSet(false)
      setMapContainer(null)
    }
  }, [showHiddenDisplay])

  const setMapNode = (node) => {
    if (!node) return;
    // NOTE: Because we don't unset the old map, we almost always have a mapContainer present
    //       This extra boolean state allows us to track when it's set on each map page load
    setIsNewLocalContainerSet(true)
    setMapContainer(node)
  }

  return (
    <>
      <NearbyTopNav />
      {!showHiddenDisplay && (
        <PageContainer>
          <MapContainer
            id='map-container'
            ref={setMapNode}
            style={{ visibility: profilesInitialized && 'visible' }}
          />
        </PageContainer>
      )}
      {!showHiddenDisplay && (!profilesInitialized || !isWebsocketConnected || !mapDataIsInitialized) &&
        <MapLoader
          isLocationPermissionDenied={isLocationPermissionDenied}
          isWebsocketConnected={isWebsocketConnected}
          mapDataIsInitialized={mapDataIsInitialized}
          profilesInitialized={profilesInitialized}
        />
      }
      {!showHiddenDisplay && isLocationPermissionDenied &&
        <LocationDeniedContainer>
          <LocationPermissionDenied />
        </LocationDeniedContainer>
      }
      {showHiddenDisplay && <HiddenDisplay />}
    </>
  )
}

export default Nearby
